import { type MutableRefObject, type RefCallback } from 'react'

import { type SanityFrameBuilderStrings } from '@frame-builder/data/sanity/queries/types/strings'

type Object<T> = Record<string, T>

type Objects<T> = {
  [x: string]: T
}[]

type RefType<T> = MutableRefObject<T> | RefCallback<T> | null

/**
 * Shares an instance between 2 refs.
 */
export const shareRef = <T>(
  refA: RefType<T | null>,
  refB: RefType<T | null>,
): RefCallback<T> => {
  return (instance) => {
    if (refA && typeof refA === 'function') {
      refA(instance)
    }

    if (refA && typeof refA !== 'function') {
      refA.current = instance
    }

    if (refB && typeof refB === 'function') {
      refB(instance)
    }

    if (refB && typeof refB !== 'function') {
      refB.current = instance
    }
  }
}

/**
 * Clamps a value between minimum and maximum.
 */
export const clamp = (value: number, min: number, max: number) => {
  return min > value ? min : max < value ? max : value
}

/**
 * Converts an array of objects into a single object, maintaining all key and value pairs.
 */
export const toRecord = <T>(objects: Objects<T>): Object<T> => {
  return Object.assign({}, ...objects)
}

/**
 * Gets item title text.
 */
export function getItemTitle(
  frameBuilderStrings: SanityFrameBuilderStrings,
  title: string,
  discountPercent?: number | null,
) {
  if (!discountPercent) {
    return title
  }

  return [
    title,
    `${frameBuilderStrings.discountPercentLabel}`.replace(
      '{value}',
      `${discountPercent}`,
    ),
  ].join(' - ')
}
